"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import type { HeadingItem } from "./table-of-contents";
import { slugifyHeading } from "./slugifyHeading";

const headingSelector = "h1, h2, h3, h4, h5, h6";
export const AUTO_COLLAPSE_SCROLL_THRESHOLD = 240;
const BASE_AUTO_COLLAPSE_LOCK_MS = 600;
const EXTRA_LOCK_MS_PER_PX = 0.6; // 0.4ms per px ≈ 400ms per 1000px
const DESKTOP_MEDIA_QUERY = "(min-width: 1280px)";
const TOC_COLLAPSE_SETTLE_MS = 480; // duration-300 + cushion

export function shouldAutoCollapse(scrollY: number): boolean {
  return scrollY > AUTO_COLLAPSE_SCROLL_THRESHOLD;
}

function headingsAreEqual(a: HeadingItem[], b: HeadingItem[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    const left = a[index]!;
    const right = b[index]!;
    if (left.id !== right.id) return false;
    if (left.level !== right.level) return false;
    if (left.title !== right.title) return false;
  }
  return true;
}

export function extractHeadingTitle(element: HTMLHeadingElement): string {
  const clone = element.cloneNode(true) as HTMLHeadingElement;

  for (const math of clone.querySelectorAll<HTMLElement>(".katex")) {
    const visualText =
      math.querySelector<HTMLElement>(".katex-html")?.textContent;
    const fallbackText = math.getAttribute("aria-label");
    math.replaceWith(
      clone.ownerDocument.createTextNode(visualText ?? fallbackText ?? "")
    );
  }

  for (const hidden of clone.querySelectorAll<HTMLElement>(
    '[hidden], [aria-hidden="true"]'
  )) {
    hidden.remove();
  }

  return (clone.textContent ?? "").replace(/\s+/g, " ").trim();
}

export function extractHeadings(container: HTMLElement): HeadingItem[] {
  const elements = Array.from(
    container.querySelectorAll<HTMLHeadingElement>(headingSelector)
  );
  const usedIds = new Set<string>();
  const slugCounts = new Map<string, number>();
  const headings: HeadingItem[] = [];

  elements.forEach((element, index) => {
    if (element.closest(".not-prose")) return;

    const text = extractHeadingTitle(element);
    if (!text) return;

    const level = Number(element.tagName.replace("H", ""));
    if (!Number.isFinite(level)) return;

    let id = element.id.trim();
    if (!id || usedIds.has(id)) {
      const base = slugifyHeading(text) || `section-${index + 1}`;
      const currentCount = slugCounts.get(base) ?? 0;
      const nextCount = currentCount + 1;
      slugCounts.set(base, nextCount);
      id = nextCount === 1 ? base : `${base}-${nextCount}`;
      element.id = id;
    }

    usedIds.add(id);
    element.style.scrollMarginTop = "6.5rem";

    headings.push({
      id,
      level,
      title: text,
    });
  });

  return headings;
}

function labGuideHeadingClearance(): number | null {
  const guideToc = document.querySelector<HTMLElement>(
    '[data-lab-guide-toc="true"]'
  );
  if (guideToc && guideToc.getBoundingClientRect().height > 0) {
    const stickyTop = Number.parseFloat(window.getComputedStyle(guideToc).top) || 0;
    return Math.max(104, stickyTop + guideToc.getBoundingClientRect().height + 16);
  }
  return null;
}

export function scrollToHeading(id: string) {
  if (typeof window === "undefined") return;
  const target = document.getElementById(id);
  if (!target) return;

  // Navigation and the active-section marker share the same visible boundary.
  const clearance = labGuideHeadingClearance();
  target.style.scrollMarginTop = clearance === null ? "6.5rem" : `${clearance}px`;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  target.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    block: "start",
  });
}

type UseTableOfContentsArgs = {
  articleId: string;
  slug: string;
};

type UseTableOfContentsResult = {
  headings: HeadingItem[];
  currentHeadingId: string | null;
  isTocCollapsed: boolean;
  isAutoCollapseEnabled: boolean;
  handleNavigate: (id: string) => void;
  handleToggleCollapse: () => void;
  handleAutoCollapseToggle: (enabled: boolean) => void;
  hasResolvedHeadings: boolean;
};

export function useTableOfContents({
  articleId,
  slug,
}: UseTableOfContentsArgs): UseTableOfContentsResult {
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [isTocCollapsed, setIsTocCollapsed] = useState(false);
  const [isAutoCollapseEnabled, setIsAutoCollapseEnabled] = useState(true);
  const [currentHeadingId, setCurrentHeadingId] = useState<string | null>(null);
  const [hasResolvedHeadings, setHasResolvedHeadings] = useState(false);

  const articleElementRef = useRef<HTMLElement | null>(null);
  const autoCollapseLockRef = useRef(false);
  const autoCollapseLockTimer = useRef<number | null>(null);
  const lastNavigatedHeadingIdRef = useRef<string | null>(null);
  const pendingRecenterHeadingIdRef = useRef<string | null>(null);

  const ensureArticleElement = useCallback(() => {
    if (typeof document === "undefined") return null;
    const element = document.getElementById(articleId);
    articleElementRef.current = element ?? null;
    return articleElementRef.current;
  }, [articleId]);

  const updateCurrentHeadingHighlight = useCallback(() => {
    if (typeof window === "undefined") return;
    if (headings.length === 0) {
      setCurrentHeadingId(null);
      return;
    }

    const TOP_THRESHOLD = Math.max(140, (labGuideHeadingClearance() ?? 0) + 1);
    let activeId: string | null = headings[0]?.id ?? null;

    for (const heading of headings) {
      const element = document.getElementById(heading.id);
      if (!element) continue;
      const top = element.getBoundingClientRect().top;
      if (top <= TOP_THRESHOLD) {
        activeId = heading.id;
      } else {
        break;
      }
    }

    if (!autoCollapseLockRef.current && lastNavigatedHeadingIdRef.current) {
      const pendingId = lastNavigatedHeadingIdRef.current;
      if (pendingId && pendingId !== activeId) {
        lastNavigatedHeadingIdRef.current = null;
      }
    }

    setCurrentHeadingId((previous) =>
      previous === activeId ? previous : activeId
    );
  }, [headings]);

  const evaluateAutoCollapseAtCurrentPosition = useCallback(
    (query?: MediaQueryList | null) => {
      if (typeof window === "undefined") return;

      const mediaQuery = query ?? window.matchMedia(DESKTOP_MEDIA_QUERY);

      if (!mediaQuery.matches) {
        setIsTocCollapsed(false);
        pendingRecenterHeadingIdRef.current = null;
        return;
      }

      if (!isAutoCollapseEnabled) {
        return;
      }

      if (autoCollapseLockRef.current) return;

      const shouldCollapse = shouldAutoCollapse(window.scrollY);

      if (shouldCollapse) {
      }

      setIsTocCollapsed((previous) => {
        if (previous === shouldCollapse) {
          return previous;
        }

        if (shouldCollapse) {
          pendingRecenterHeadingIdRef.current =
            lastNavigatedHeadingIdRef.current ?? null;
        } else {
          pendingRecenterHeadingIdRef.current = null;
        }

        return shouldCollapse;
      });

      updateCurrentHeadingHighlight();
    },
    [isAutoCollapseEnabled, updateCurrentHeadingHighlight]
  );

  const lockAutoCollapseDuringNavigation = useCallback(
    (lockDurationMs = BASE_AUTO_COLLAPSE_LOCK_MS) => {
      if (!isAutoCollapseEnabled) return;
      if (typeof window === "undefined") return;

      autoCollapseLockRef.current = true;
      setIsTocCollapsed(false);

      if (autoCollapseLockTimer.current) {
        window.clearTimeout(autoCollapseLockTimer.current);
      }

      autoCollapseLockTimer.current = window.setTimeout(() => {
        autoCollapseLockRef.current = false;
        autoCollapseLockTimer.current = null;
        evaluateAutoCollapseAtCurrentPosition();
      }, lockDurationMs);
    },
    [evaluateAutoCollapseAtCurrentPosition, isAutoCollapseEnabled]
  );

  const handleNavigate = useCallback(
    (id: string) => {
      lastNavigatedHeadingIdRef.current = id;
      pendingRecenterHeadingIdRef.current = id;
      const elementRect = document.getElementById(id)?.getBoundingClientRect();
      const distanceToTarget = elementRect ? Math.abs(elementRect.top) : 0;
      const dynamicLock = Math.min(
        2000,
        Math.max(
          BASE_AUTO_COLLAPSE_LOCK_MS,
          BASE_AUTO_COLLAPSE_LOCK_MS + distanceToTarget * EXTRA_LOCK_MS_PER_PX
        )
      );
      lockAutoCollapseDuringNavigation(dynamicLock);
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          scrollToHeading(id);
          setCurrentHeadingId(id);
        });
      } else {
        scrollToHeading(id);
        setCurrentHeadingId(id);
      }
    },
    [lockAutoCollapseDuringNavigation]
  );

  const handleToggleCollapse = useCallback(() => {
    if (typeof window !== "undefined" && autoCollapseLockTimer.current) {
      window.clearTimeout(autoCollapseLockTimer.current);
    }
    autoCollapseLockTimer.current = null;
    autoCollapseLockRef.current = false;
    pendingRecenterHeadingIdRef.current = null;
    lastNavigatedHeadingIdRef.current = null;
    setIsAutoCollapseEnabled(false);
    setIsTocCollapsed((previous) => !previous);
  }, []);

  const handleAutoCollapseToggle = useCallback(
    (enabled: boolean) => {
      setIsAutoCollapseEnabled(enabled);

      if (!enabled) {
        if (typeof window !== "undefined" && autoCollapseLockTimer.current) {
          window.clearTimeout(autoCollapseLockTimer.current);
        }
        autoCollapseLockTimer.current = null;
        autoCollapseLockRef.current = false;
        pendingRecenterHeadingIdRef.current = null;
        lastNavigatedHeadingIdRef.current = null;
        setIsTocCollapsed(false);
        return;
      }

      evaluateAutoCollapseAtCurrentPosition();
    },
    [evaluateAutoCollapseAtCurrentPosition]
  );

  useEffect(() => {
    setIsTocCollapsed(false);
    setIsAutoCollapseEnabled(true);
    setHasResolvedHeadings(false);
    autoCollapseLockRef.current = false;
    lastNavigatedHeadingIdRef.current = null;
    pendingRecenterHeadingIdRef.current = null;
    articleElementRef.current = ensureArticleElement();

    if (typeof window !== "undefined") {
      if (autoCollapseLockTimer.current) {
        window.clearTimeout(autoCollapseLockTimer.current);
        autoCollapseLockTimer.current = null;
      }

      if (!window.location.hash) {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  }, [slug, ensureArticleElement]);

  useEffect(() => {
    const articleElement = ensureArticleElement();
    if (!articleElement) return undefined;

    const updateHeadings = () => {
      const container = articleElementRef.current;
      if (!container) return;
      const nextHeadings = extractHeadings(container);
      setHeadings((previous) =>
        headingsAreEqual(previous, nextHeadings) ? previous : nextHeadings
      );
      setHasResolvedHeadings(true);
    };

    updateHeadings();

    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(() => {
        updateHeadings();
      });
      observer.observe(articleElement, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }

    return undefined;
  }, [ensureArticleElement, slug]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && autoCollapseLockTimer.current) {
        window.clearTimeout(autoCollapseLockTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    let frame = 0;

    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        pendingRecenterHeadingIdRef.current = null;
      }
      const targetList =
        (event.currentTarget as MediaQueryList | null) ??
        (event.target as MediaQueryList | null);
      evaluateAutoCollapseAtCurrentPosition(targetList);
    };

    const handlePointer = () => {
      evaluateAutoCollapseAtCurrentPosition(mediaQuery);

      if (!pendingRecenterHeadingIdRef.current) return;
      const id = pendingRecenterHeadingIdRef.current;
      if (!id) return;
      const element = document.getElementById(id);
      if (!element) return;

      const { top } = element.getBoundingClientRect();
      const withinViewport = top > 0 && top < window.innerHeight;
      if (withinViewport) {
        pendingRecenterHeadingIdRef.current = null;
        return;
      }

      if (!frame) {
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          scrollToHeading(id);
          pendingRecenterHeadingIdRef.current = null;
        });
      }
    };

    evaluateAutoCollapseAtCurrentPosition(mediaQuery);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaChange);
    } else {
      mediaQuery.addListener?.(handleMediaChange);
    }

    window.addEventListener("scroll", handlePointer, { passive: true });

    return () => {
      window.removeEventListener("scroll", handlePointer);
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleMediaChange);
      } else {
        mediaQuery.removeListener?.(handleMediaChange);
      }
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [
    evaluateAutoCollapseAtCurrentPosition,
    headings.length,
    isAutoCollapseEnabled,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (headings.length === 0) {
      setCurrentHeadingId(null);
      return undefined;
    }

    let frame = 0;
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateCurrentHeadingHighlight();
      });
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [headings, updateCurrentHeadingHighlight]);

  useEffect(() => {
    if (headings.length === 0) {
      setIsAutoCollapseEnabled(true);
      setIsTocCollapsed(false);
      setCurrentHeadingId(null);
    }
  }, [headings.length]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!currentHeadingId) return;

    const container = document.querySelector('[data-toc-container="desktop"]');
    const activeLink = container?.querySelector(
      `[data-heading-id="${currentHeadingId}"]`
    );

    if (
      !(container instanceof HTMLElement) ||
      !(activeLink instanceof HTMLElement)
    ) {
      return;
    }

    const linkOffsetTop = activeLink.offsetTop;
    const linkOffsetBottom = linkOffsetTop + activeLink.offsetHeight;
    const visibleTop = container.scrollTop;
    const visibleBottom = visibleTop + container.clientHeight;
    const buffer = 24;

    if (linkOffsetTop < visibleTop + buffer) {
      container.scrollTo({
        top: Math.max(linkOffsetTop - container.clientHeight * 0.25, 0),
        behavior: "auto",
      });
    } else if (linkOffsetBottom > visibleBottom - buffer) {
      const maxScroll = Math.max(
        container.scrollHeight - container.clientHeight,
        0
      );
      container.scrollTo({
        top: Math.min(
          Math.max(linkOffsetBottom - container.clientHeight * 0.75, 0),
          maxScroll
        ),
        behavior: "auto",
      });
    }
  }, [currentHeadingId]);

  useEffect(() => {
    if (!isTocCollapsed) return;
    if (typeof window === "undefined") return;

    const targetId =
      pendingRecenterHeadingIdRef.current ?? lastNavigatedHeadingIdRef.current;
    if (!targetId) return;

    let attempts = 0;
    const desiredOffset = 104;

    const adjust = () => {
      const elementInstance = document.getElementById(targetId);
      if (!elementInstance) {
        pendingRecenterHeadingIdRef.current = null;
        if (autoCollapseLockTimer.current) {
          window.clearTimeout(autoCollapseLockTimer.current);
          autoCollapseLockTimer.current = null;
        }
        autoCollapseLockRef.current = false;
        return;
      }

      const rect = elementInstance.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const targetScroll = absoluteTop - desiredOffset;

      const doc = document.documentElement;
      const maxScroll = Math.max(doc.scrollHeight - window.innerHeight, 0);
      const clampedScroll = Math.min(Math.max(targetScroll, 0), maxScroll);

      window.scrollTo({ top: clampedScroll, behavior: "auto" });

      const newRect = elementInstance.getBoundingClientRect();
      const remaining = Math.abs(newRect.top - desiredOffset);

      attempts += 1;
      if (remaining <= 4 || attempts >= 6) {
        pendingRecenterHeadingIdRef.current = null;
        if (autoCollapseLockTimer.current) {
          window.clearTimeout(autoCollapseLockTimer.current);
          autoCollapseLockTimer.current = null;
        }
        autoCollapseLockRef.current = false;
        evaluateAutoCollapseAtCurrentPosition();
        return;
      }

      window.requestAnimationFrame(adjust);
    };

    const timeout = window.setTimeout(() => {
      adjust();
    }, TOC_COLLAPSE_SETTLE_MS);

    return () => {
      window.clearTimeout(timeout);
      if (autoCollapseLockTimer.current) {
        window.clearTimeout(autoCollapseLockTimer.current);
        autoCollapseLockTimer.current = null;
      }
      autoCollapseLockRef.current = false;
      pendingRecenterHeadingIdRef.current = null;
    };
  }, [evaluateAutoCollapseAtCurrentPosition, isTocCollapsed]);

  return {
    headings,
    currentHeadingId,
    isTocCollapsed,
    isAutoCollapseEnabled,
    handleNavigate,
    handleToggleCollapse,
    handleAutoCollapseToggle,
    hasResolvedHeadings,
  };
}

export { slugifyHeading } from "./slugifyHeading";
